import styled from 'styled-components';
import { Table, Form, Row, Pagination, Col } from 'react-bootstrap';
import { ToReadableDate, ToCurrencyFormat, generateTimestampUtil } from '../../../utils';
import { useState, useRef, useEffect } from 'react';
import { ClaimsGetService, ClaimReportGetService } from '../../../domain';
import { PaginationComponent } from '.';
import Swal from 'sweetalert2';

const _ClaimsGetService = new ClaimsGetService();
const _ClaimsReportGetService = new ClaimReportGetService();

const SubDescription = styled.div`
    font-size: 15px;
    font-weight: 300;
    padding: 5px 0px;
`;

const TableContainer = styled.div`
    overflow-x: auto;
    white-space: nowrap;
    padding: 0;
    margin: 0;
    width: 100%;
`;

const Title = styled.h1`
    font-size: 34px;
    font-weight: 700;
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: flex-end;

    @media (min-width: 300px) and (max-width: 500px) {
        justify-content: center;
    }
`;

export default function ShowPolicyView({ formData, setFormData, setLoading, getPoliciesData }) {
    const policyCheckboxesRef = useRef({});
    const processRef = (element, id) => {
        if (element) {
            policyCheckboxesRef.current[id] = element;
            if (formData.selectedPolicy === id) {
                policyCheckboxesRef.current[id].checked = true;
            }
        }
    };

    const bindAvailableClaims = async (id) => {
        const claims = await _ClaimsGetService.execute({ id: id, claimsStaff: true });
        return claims;
    };

    const handlePolicySelection = async (e, event) => {
        try {
            if (event.id === formData.selectedPolicy) {
                e.preventDefault();
                return;
            }

            setLoading(true);
            const claims = await bindAvailableClaims(event.id);
            console.log('claims: ', claims);
            setLoading(false);

            if (formData?.selectedPolicy !== event.id) {
                const { customerDetails, ...policyCustomerDetails } = formData.availablePolicy.uploadedPolicies.find(
                    (item) => item.id === event.id
                );

                setFormData({
                    ...formData,
                    selectedPolicy: event.id,
                    availableClaims: claims,
                    createNewClaim: false,
                    selectedClaim: claims.claims.length ? undefined : 'create new claim',
                    claimsPayload: {
                        refId: event.id,
                        refTable: 'txn_uploaded_policy',
                        status: 'Pending',
                        customerDetails: {
                            ...customerDetails,
                            ...policyCustomerDetails
                        }
                    }
                });

                Object.keys(policyCheckboxesRef.current).forEach((key) => {
                    if (key !== event.id.toString()) {
                        policyCheckboxesRef.current[key].checked = false;
                    } else {
                        policyCheckboxesRef.current[key].checked = true;
                    }
                });
            }
        } catch (error) {
            console.log(error);
            setLoading(false);
            Swal.fire({
                text: 'Something went wrong. Please try again later.',
                icon: 'error',
                iconColor: 'black',
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Ok',
                customClass: {
                    popup: 'my-popup',
                    confirmButton: 'my-confirm-button',
                    cancelButton: 'my-cancel-button'
                },
                allowOutsideClick: false
            });
        }
    };

    const handleReportExtraction = async (e) => {
        try {
            e.preventDefault();
            setLoading(true);
            const claims = await _ClaimsReportGetService.execute();

            if (!claims?.excelFile) {
                Swal.fire({
                    text: 'Sorry. Something went wrong. Please try again later.',
                    icon: 'warning',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: 'OK',
                    customClass: {
                        popup: 'my-popup',
                        confirmButton: 'my-confirm-button',
                        cancelButton: 'my-cancel-button'
                    },
                    allowOutsideClick: false
                });
                setLoading(false);
                return;
            }

            const link = document.createElement('a');
            link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${claims.excelFile}`;
            link.download = `Claim Report-${generateTimestampUtil()}.xlsx`;
            link.click();

            Swal.fire({
                text: 'Claims Report Downloaded.',
                icon: 'success',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'OK',
                customClass: {
                    popup: 'my-popup',
                    confirmButton: 'my-confirm-button',
                    cancelButton: 'my-cancel-button'
                },
                allowOutsideClick: false
            });

            setLoading(false);
        } catch (error) {
            console.log(error);
            Swal.fire({
                text: 'Sorry. Something went wrong. Please try again later.',
                icon: 'warning',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'OK',
                customClass: {
                    popup: 'my-popup',
                    confirmButton: 'my-confirm-button',
                    cancelButton: 'my-cancel-button'
                },
                allowOutsideClick: false
            });
            setLoading(false);
        }
    };

    useEffect(() => {
        if (formData && Object.keys(formData)?.length === 1) {
            Object.keys(policyCheckboxesRef.current).forEach((key) => {
                if (policyCheckboxesRef.current[key]) {
                    policyCheckboxesRef.current[key].checked = false;
                }
            });
        }
    }, [formData]);

    return (
        <div>
            <br />

            {formData?.availablePolicy.uploadedPolicies && (
                <Col lg={12} className="d-flex justify-content-end mb-2">
                    <a
                        href="#"
                        onClick={(e) => {
                            handleReportExtraction(e);
                        }}
                    >
                        <i className="bi bi-file-earmark-spreadsheet">Extract Claims Report</i>
                    </a>
                </Col>
            )}
            <TableContainer>
                <Table bordered hover>
                    <thead className="text-center">
                        <tr>
                            <th></th>
                            <th>Loan Account Number</th>
                            <th>Effective Date</th>
                            <th>Expiry Date</th>
                            <th>First Name</th>
                            <th>Last Name</th>
                            <th>Email</th>
                            <th>Mobile No.</th>
                            <th>House Number & Street Name</th>
                            <th>Barangay</th>
                            <th>Municipality/City</th>
                            <th>Province</th>
                            <th>Zip Code</th>
                        </tr>
                    </thead>
                    <tbody className="text-center">
                        {formData?.availablePolicy.uploadedPolicies &&
                            formData.availablePolicy.uploadedPolicies.map((item, index) => {
                                return (
                                    <tr key={'policyItem_' + index}>
                                        <td className="text-center">
                                            {formData.availablePolicy.length === 1 ? (
                                                <Form.Check
                                                    className="checkbox"
                                                    style={{ margin: '3px 5px 0px 7px' }}
                                                    type="checkbox"
                                                    name="agreed"
                                                    defaultChecked
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                    }}
                                                ></Form.Check>
                                            ) : (
                                                <Form.Check
                                                    className="checkbox"
                                                    style={{ margin: '3px 5px 0px 7px' }}
                                                    type="checkbox"
                                                    name="agreed"
                                                    ref={(el) => processRef(el, item.id)}
                                                    onClick={(e) => handlePolicySelection(e, item)}
                                                ></Form.Check>
                                            )}
                                        </td>
                                        <td>{item.coicNo}</td>
                                        <td>{ToReadableDate(item.inceptionDate)}</td>
                                        <td>{ToReadableDate(item.expiryDate)}</td>
                                        <td>{item.firstName}</td>
                                        <td>{item.lastName}</td>
                                        <td>{item.email}</td>
                                        <td>{item.mobileNo}</td>
                                        <td>{item.street}</td>
                                        <td>{item.barangay}</td>
                                        <td>{item.city}</td>
                                        <td>{item.province}</td>
                                        <td>{item.zipCode}</td>
                                    </tr>
                                );
                            })}
                        {!formData?.availablePolicy?.uploadedPolicies?.length && (
                            <tr className="text-center">
                                <td colSpan="13">No policy available.</td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </TableContainer>

            <br />

            {formData?.availablePolicy?.totalData > 5 && (
                <ButtonContainer>
                    <PaginationComponent
                        totalPage={formData?.availablePolicy?.totalPage}
                        currentPage={formData?.availablePolicy?.currentPage}
                        onPageChange={getPoliciesData}
                    />
                </ButtonContainer>
            )}
        </div>
    );
}
